import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useWebSocketContext } from "../../contexts/WebSocketProvider";
import useAddSettlement from "../../hooks/useAddSettlementAmount";
import { formatINR } from "../../utils/formatINR";

export default function SettlementModal({ isOpen, onClose, onSubmitSuccess }) {
  const { send } = useWebSocketContext();
  const { addSettlement, isLoading: isSubmitting, error: submitError } = useAddSettlement();

  // --- State ---
  const [groupId, setGroupId] = useState("");
  const [selectedMonthYear, setSelectedMonthYear] = useState(""); 
  const [settlementDate, setSettlementDate] = useState(new Date().toISOString().split('T')[0]); 
  const [totalDue, setTotalDue] = useState(0);
  const [stations, setStations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // --- Memoize send function to prevent unnecessary effect re-runs ---
  const fetchGroupData = useCallback(async () => {
    // Basic validation before fetching
    if (!groupId?.trim() || !selectedMonthYear) {
      setStations([]);
      setTotalDue(0);
      return;
    }

    const parts = selectedMonthYear.split("-");
    if (parts.length < 2) return;
    
    const [year, month] = parts;
    setIsLoading(true);

    try {
      const response = await send("cms_get_all_stations_by_group", {
        group_id: groupId.trim().toUpperCase(),
        month: parseInt(month, 10),
        year: parseInt(year, 10),
      });

      if (response?.success && response.data) {
        const fetchedStations = response.data.map((id) => ({
          id: id,
          entries: [{ day: "", amount: "" }],
        }));
        setStations(fetchedStations);
        setTotalDue(response.amount_to_settle ?? 0);
      } else {
        // Handle unsuccessful response
        setStations([]);
        setTotalDue(0);
      }
    } catch (err) {
      console.error("Failed to fetch group stations:", err);
      setStations([]);
      setTotalDue(0);
    } finally {
      setIsLoading(false);
    }
  }, [groupId, selectedMonthYear, send]);

  // --- Effects ---
  useEffect(() => {
    const timeoutId = setTimeout(fetchGroupData, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchGroupData]);

  // --- Reset state when modal closes ---
  useEffect(() => {
    if (!isOpen) {
      // Small delay to prevent state updates during close animation
      const timer = setTimeout(() => {
        setGroupId("");
        setSelectedMonthYear("");
        setSettlementDate(new Date().toISOString().split('T')[0]);
        setTotalDue(0);
        setStations([]);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // --- Logic Functions ---
  const handleSubmit = async () => {
    const stationsMap = {};
    
    // Parse settlement month/year
    const [year, month] = selectedMonthYear.split("-");
    
    stations.forEach((station) => {
      const dateAmountMap = {};
      station.entries.forEach((entry) => {
        if (entry.day && entry.amount && Number(entry.amount) > 0) {
          // Construct full date: YYYY-MM-DD
          const fullDate = `${year}-${month}-${String(entry.day).padStart(2, '0')}`;
          dateAmountMap[fullDate] = parseFloat(entry.amount);
        }
      });
      
      if (Object.keys(dateAmountMap).length > 0) {
        stationsMap[station.id] = dateAmountMap;
      }
    });

    // Validate that at least one station has entries
    if (Object.keys(stationsMap).length === 0) {
      alert("Please add at least one payment entry before submitting.");
      return;
    }

    const payload = {
      group_id: groupId.trim().toUpperCase(),
      settlement_month: selectedMonthYear,
      verification_date: settlementDate,
      stations: stationsMap,
    };

    try {
      await addSettlement(payload);
      // Execute success callback before closing to ensure parent updates
      if (onSubmitSuccess) onSubmitSuccess();
      onClose(); 
    } catch (err) {
      console.error("Submission failed:", err);
    }
  };

  const addDateEntry = useCallback((stationIndex) => {
    setStations((prev) =>
      prev.map((station, i) =>
        i === stationIndex
          ? { ...station, entries: [...(station.entries || []), { day: "", amount: "" }] }
          : station
      )
    );
  }, []);

  const updateEntry = useCallback((sIdx, eIdx, field, value) => {
    setStations((prev) =>
      prev.map((station, i) => {
        if (i !== sIdx) return station;
        const newEntries = (station.entries || []).map((entry, j) =>
          j === eIdx ? { ...entry, [field]: value } : entry
        );
        return { ...station, entries: newEntries };
      })
    );
  }, []);

  // Generate days array (1-31) based on selected month
  const daysInMonth = useMemo(() => {
    if (!selectedMonthYear) return [];
    const [year, month] = selectedMonthYear.split("-");
    const daysCount = new Date(parseInt(year), parseInt(month), 0).getDate();
    return Array.from({ length: daysCount }, (_, i) => i + 1);
  }, [selectedMonthYear]);

  // --- Calculations (Memoized) ---
  const totalPaid = useMemo(() => {
    return (stations || []).reduce(
      (acc, s) => acc + (s.entries || []).reduce((sAcc, e) => sAcc + (Number(e.amount) || 0), 0),
      0
    );
  }, [stations]);

  const amountRemaining = useMemo(() => (totalDue ?? 0) - totalPaid, [totalDue, totalPaid]);
  const settlementType = useMemo(() => amountRemaining <= 0 ? "full" : "partial", [amountRemaining]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-sans text-slate-900">
      <div className="bg-white w-full max-w-6xl p-8 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        
        {/* HEADER SECTION */}
        <div className="flex flex-wrap justify-between items-start gap-6 mb-8">
          <div className="space-y-4">
            <div className="relative">
              <label className="block text-[10px] font-black text-slate-400 mb-1 uppercase tracking-widest">Group ID</label>
              <input
                placeholder="Ex: GRP-100"
                className="text-2xl p-2 border-b-2 border-slate-100 focus:border-blue-600 outline-none w-64 uppercase font-bold"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
              />
              {isLoading && (
                <div className="absolute right-0 top-10 animate-spin h-5 w-5 border-2 border-t-blue-600 border-slate-200 rounded-full" />
              )}
            </div>
            <div className="bg-slate-50 border border-slate-100 px-4 py-2 rounded-lg text-sm inline-block">
              Amount Due: <span className="font-bold text-red-600 ml-1">{formatINR(totalDue ?? 0)}</span>
            </div>
          </div>

          <div className="flex gap-6">
            <div>
              <label className="block text-[10px] font-black text-slate-400 mb-1 uppercase tracking-widest">Verification Date</label>
              <input 
                type="date" 
                className="p-2 border rounded-xl bg-slate-50 outline-none focus:ring-2 focus:ring-blue-600 transition"
                value={settlementDate}
                onChange={(e) => setSettlementDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 mb-1 uppercase tracking-widest">Settlement Month</label>
              <input 
                type="month" 
                className="p-2 border rounded-xl bg-slate-50 outline-none focus:ring-2 focus:ring-blue-600 transition"
                value={selectedMonthYear}
                onChange={(e) => setSelectedMonthYear(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* STATION COLUMNS */}
        <div className="flex gap-4 overflow-x-auto pb-6 min-h-[350px]">
          {(stations || []).length === 0 && !isLoading ? (
            <div className="w-full flex items-center justify-center border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 bg-slate-50">
              {(!groupId || !selectedMonthYear) 
                ? "Enter details above to populate stations" 
                : "No data available for this selection"}
            </div>
          ) : (
            (stations || []).map((station, sIdx) => (
              <div key={station.id} className="min-w-[280px] bg-white p-5 rounded-2xl border border-slate-200 flex-shrink-0 shadow-sm hover:shadow-md transition">
                <div className="flex justify-between items-center mb-4 border-b border-slate-50 pb-3">
                  <span className="font-bold text-sm text-slate-700 uppercase">{station.id}</span>
                  <span className="text-[9px] bg-blue-100 text-blue-700 px-2 py-1 rounded-md font-bold uppercase">Station</span>
                </div>
                
                <div className="space-y-3">
                  {(station.entries || []).map((entry, eIdx) => (
                    <div key={eIdx} className="flex gap-2">
                      <select 
                        className="text-[11px] p-2 border rounded-lg w-1/2 bg-slate-50 outline-none focus:bg-white focus:ring-1 focus:ring-black"
                        value={entry.day}
                        onChange={(e) => updateEntry(sIdx, eIdx, 'day', e.target.value)}
                        disabled={!selectedMonthYear}
                      >
                        <option value="">Day</option>
                        {daysInMonth.map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                      <input 
                        type="number" 
                        placeholder="Amount"
                        className="text-[11px] p-2 border rounded-lg w-1/2 bg-slate-50 outline-none focus:bg-white focus:ring-1 focus:ring-black"
                        value={entry.amount}
                        onChange={(e) => updateEntry(sIdx, eIdx, 'amount', e.target.value)}
                      />
                    </div>
                  ))}
                  <button 
                    onClick={() => addDateEntry(sIdx)}
                    className="w-full py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-slate-500 text-[10px] transition font-bold uppercase border border-slate-100"
                  >
                    + Add Date Row
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* FOOTER SUMMARY */}
        <div className="mt-8 pt-8 border-t border-slate-100 flex flex-wrap justify-between items-end gap-6">
          <div className="flex gap-12">
            <div>
              <p className="text-[10px] uppercase text-slate-400 font-black tracking-widest mb-2">Total Paid</p>
              <p className="text-4xl font-bold text-slate-900">{formatINR(totalPaid ?? 0)}</p>
            </div>
            <div className="border-l border-slate-100 pl-12">
              <p className="text-[10px] uppercase text-slate-400 font-black tracking-widest mb-2">Balance</p>
              <p className={`text-4xl font-bold ${amountRemaining <= 0 ? 'text-green-600' : 'text-slate-900'}`}>
                {formatINR(Math.max(0, amountRemaining) ?? 0)}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-5">
             {submitError && <span className="text-xs text-red-500 font-bold">Error: {submitError}</span>}
             
             <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-tighter border ${
               settlementType === 'full' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-orange-50 border-orange-200 text-orange-700'
             }`}>
               {settlementType} Settlement
             </div>
             
             <div className="flex gap-4">
               <button onClick={onClose} className="px-6 py-2.5 text-sm font-bold text-slate-400 hover:text-red-500 transition">
                 Discard
               </button>
               <button 
                 disabled={isSubmitting || (stations || []).length === 0 || !selectedMonthYear}
                 onClick={handleSubmit}
                 className="px-12 py-3 bg-blue-600 text-white text-sm font-black rounded-2xl hover:bg-blue-700 transition disabled:opacity-30 disabled:grayscale shadow-lg shadow-blue-200 active:scale-95"
               >
                 {isSubmitting ? "Submitting..." : "Confirm & Save"}
               </button>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}